'use client';

import type {
  BehavioralEventName,
  ConversionType,
} from '../../lib/analytics/conversions';
import { INTERNAL_ONLY_EVENTS } from '../../lib/analytics/types';

/**
 * Maps behavioral event names to their legacy ConversionType values.
 * When a behavioral event fires, we also push a legacy CONVERSION + e_conversionClick
 * pair to dataLayer for GTM backward compatibility.
 *
 * Not all BehavioralEventName values need a GTM mapping — only conversion-related
 * events are included here. Unlisted events are silently skipped at runtime.
 *
 * VIEWED_PROFILE: legacy View Profile conversion is only emitted for search-result
 * card clicks (`linkContextPage === 'SearchResults'`). Profile page-load
 * VIEWED_PROFILE still goes to dataLayer as itself, but must not dual-fire the
 * legacy pair (that would double-count vs the click Montefiore measures).
 */
export const GTM_CONVERSION_MAP: Partial<
  Record<BehavioralEventName, ConversionType>
> = {
  VIEWED_PROFILE: 'View Profile',
  CLICKED_TO_CALL: 'Call',
  CLICKED_TO_GET_DIRECTIONS: 'Get Directions',
  CLICKED_EHR_REDIRECT: 'Initiate Schedule Appointment',
  OPENED_FULL_MODAL: 'Initiate Schedule Appointment',
  CLICKED_PROVIDER_LINK: 'Provider Link Click',
};

const shouldEmitLegacyConversion = (
  event: string,
  properties: Record<string, unknown>,
): ConversionType | undefined => {
  const legacyConversionType = GTM_CONVERSION_MAP[event as BehavioralEventName];
  if (!legacyConversionType) {
    return undefined;
  }

  if (event === 'VIEWED_PROFILE') {
    return properties.linkContextPage === 'SearchResults'
      ? legacyConversionType
      : undefined;
  }

  return legacyConversionType;
};

export interface GoogleTagManagerPluginConfig {
  debug?: boolean;
  containerId: string;
  dataLayerName?: string;
  customScriptSrc?: string;
  preview?: string;
  auth?: string;
  execution?: 'async' | 'defer';
  nonce?: string;
}

export const defaultGtmConfig: GoogleTagManagerPluginConfig = {
  debug: false,
  containerId: '',
  dataLayerName: 'dataLayer',
  preview: undefined,
  auth: undefined,
  execution: 'async',
};

interface EventPayload {
  event: string;
  anonymousId?: string;
  userId?: string;
  properties?: Record<string, any>;
}

interface GTMAnalyticsPlugin {
  name: string;
  config: GoogleTagManagerPluginConfig;
  initialize: ({ config }: { config: GoogleTagManagerPluginConfig }) => void;
  page: ({
    payload,
    options,
    instance,
    config,
  }: {
    payload: EventPayload;
    options: any;
    instance: any;
    config: GoogleTagManagerPluginConfig;
  }) => void;
  track: ({
    payload,
    options,
    config,
  }: {
    payload: EventPayload;
    options: any;
    config: GoogleTagManagerPluginConfig;
  }) => void;
  loaded: () => boolean;
}

/**
 * Google tag manager plugin
 * @link https://getanalytics.io/plugins/google-tag-manager
 * @link https://developers.google.com/tag-manager/
 * @param {object} pluginConfig - Plugin settings
 * @param {string} pluginConfig.containerId - The Container ID uniquely identifies the GTM Container.
 * @param {string} [pluginConfig.dataLayerName=dataLayer] - The optional name for dataLayer-object. Defaults to dataLayer.
 * @param {string} [pluginConfig.customScriptSrc] - Load Google Tag Manager script from a custom source
 * @param {string} [pluginConfig.preview] - The preview-mode environment
 * @param {string} [pluginConfig.auth] - The preview-mode authentication credentials
 * @param {string} [pluginConfig.execution] - The script execution mode
 * @param {string} [pluginConfig.nonce] - Content-Security-Policy nonce value
 * @return {object} Analytics plugin
 * @example
 *
 * googleTagManager({
 *   containerId: 'GTM-123xyz'
 * })
 */
function googleTagManager(
  pluginConfig: GoogleTagManagerPluginConfig,
): GTMAnalyticsPlugin {
  // Allow for userland overides of base methods
  return {
    name: 'google-tag-manager',
    config: {
      ...defaultGtmConfig,
      ...pluginConfig,
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    initialize: () => {},
    page: ({ payload }) => {
      window.dataLayer?.push({
        ...payload.properties,
        event: 'PAGE_VIEW',
      });
    },
    track: ({ payload, config }) => {
      const { anonymousId, userId, properties = {}, event } = payload;

      // Skip internal-only events - they should only go to Mixpanel
      if (
        INTERNAL_ONLY_EVENTS.includes(
          event as (typeof INTERNAL_ONLY_EVENTS)[number],
        )
      ) {
        return;
      }

      if (Array.isArray(window.dataLayer)) {
        const formattedPayload = properties;
        if (userId) {
          formattedPayload.userId = userId;
        }
        if (anonymousId) {
          formattedPayload.anonymousId = anonymousId;
        }
        if (!properties.category) {
          formattedPayload.category = 'All';
        }
        if (config.debug) {
          // eslint-disable-next-line no-console
          console.log('dataLayer push', {
            event,
            ...formattedPayload,
          });
        }
        window.dataLayer.push({
          event,
          ...formattedPayload,
        });

        // GTM backward compatibility: when a behavioral event fires,
        // also push legacy CONVERSION + e_conversionClick entries
        const legacyConversionType = shouldEmitLegacyConversion(
          event,
          formattedPayload,
        );
        if (legacyConversionType) {
          const legacyPayload = {
            ...formattedPayload,
            conversionType: legacyConversionType,
            clickText: legacyConversionType,
          };
          window.dataLayer.push({
            event: 'e_conversionClick',
            ...legacyPayload,
          });
          window.dataLayer.push({
            event: 'CONVERSION',
            ...legacyPayload,
          });
        }
      }
    },
    loaded: () => Array.prototype.push !== window.dataLayer?.push,
  };
}

export default googleTagManager;
